import dayjs from 'dayjs';
import customParseFormat from 'dayjs/plugin/customParseFormat';

import { DataTableColumn, DataTableColumnType, FilterType } from '../../../../../../common/_custom/DataTable/DataTable.types';
import { parseINR } from '../../../RevenueTables/RevenueVendorExpenseTable/components/overlap.constants';
import { DATE_FORMATS } from '../../../../../../utils/dateUtils';

dayjs.extend(customParseFormat);

/**
 * Filter/sort behaviour for the Credit Summary table (polus `GET /cibil/table`).
 *
 * Why this map exists at all: polus sends `headers: [{name, key}]` and nothing else — no type
 * information — while every cell value in `cibilData` is a pre-formatted String. `"₹ 25,00,000"`
 * and `"Active"` are indistinguishable to the client, so the filter drawer cannot know whether a
 * column wants a tick-list, a date range or a min/max pair. This map supplies exactly that, keyed
 * by the header `key` polus already sends.
 *
 * It is merged ONTO the response headers (see buildCreditSummaryColumns) rather than replacing
 * them: the response stays the source of truth for which columns exist and in what order, so a
 * column polus adds later renders as an ordinary column instead of breaking the page.
 */

export const CREDIT_SUMMARY_FILTER_SOURCE_KEY = 'CREDIT_SUMMARY';

/** The dash polus writes for any value the bureau did not report (`CibilTableRow`). */
export const MISSING_CELL_VALUE = '-';

/** Format polus formats both date columns with — `DateUtil.DD_MM_YYYY_WITH_SPACE`. */
export const CREDIT_SUMMARY_DATE_FORMAT = DATE_FORMATS.DATE_FORMAT_DD_MMM_YYYY;

export enum CreditSummaryValueType {
    TEXT = 'TEXT',
    AMOUNT = 'AMOUNT',
    DATE = 'DATE',
    DURATION = 'DURATION',
}

/** A row of the Credit Summary table. polus serialises every cell as a string,  when unreported. */
export type CreditSummaryRow = Record<string, string | null | undefined>;

export type CreditSummaryHeader = { name: string; key: string };

export enum CreditSummaryCellTone {
    DANGER_WHEN_POSITIVE = 'DANGER_WHEN_POSITIVE',
    DANGER_WHEN_PRESENT = 'DANGER_WHEN_PRESENT',
}

export const ACCOUNT_STATUS_FIELD = 'accountStatus';

export const ACTIVE_ACCOUNT_STATUS = 'Active';

type CreditSummaryColumnBehaviour = {
    valueType: CreditSummaryValueType;
    filterType?: FilterType;
    sortable?: boolean;
    /**
     * A sibling field on the row holding the raw, unformatted value — preferred over parsing the
     * display string. Only `sanctionDate` has one (`sanctionedDateActual`, a real Date that polus
     * already serializes and uses as its own tertiary sort key).
     */
    rawValueKey?: string;
    /** Rendered inside the min/max inputs by RangeFilterRenderer via `column.unit`. Keep to one
     *  character: the input's unit wrapper is squeezed to 10% width with text clipping, so anything
     *  longer ("Months") renders cut off. Longer units go in `labelSuffix` instead. */
    unit?: string;
    /** Appended to the filter card's label — e.g. "(Months)" — for units too long for the input. */
    labelSuffix?: string;
    tone?: CreditSummaryCellTone;
    /**
     * Size this column to its header and ellipsis anything longer, instead of growing to fit its
     * widest value. Only the identity columns carry it: they are the frozen ones and their values
     * are open-ended, so letting them grow pushes every figure off-screen.
     */
    truncate?: boolean;
    /**
     * Let the cell wrap onto a second line rather than sit on one long one. For the columns whose
     * values read as sentences - a run of delays, an account remark - where growing the column to
     * the longest value would make it dominate the table.
     */
    wrap?: boolean;
};

export const CREDIT_SUMMARY_COLUMNS: Record<string, CreditSummaryColumnBehaviour> = {
    // Categorical — options are the values actually present in the rows, see distinctColumnOptions.
    // NOTE `ownership` deliberately keeps its API key even though useDynamicFilterOptions assigns
    // owner options to any field starting with "owners" — `filterConfig.options` is applied after
    // that block, so ours wins. Do not rename the field to dodge it; it must match the payload.
    name: { valueType: CreditSummaryValueType.TEXT, filterType: FilterType.MULTI_SELECT, truncate: true, sortable: true },
    investorName: { valueType: CreditSummaryValueType.TEXT, filterType: FilterType.MULTI_SELECT, truncate: true, sortable: true },
    loanType: { valueType: CreditSummaryValueType.TEXT, filterType: FilterType.MULTI_SELECT, truncate: true, sortable: true },
    accountStatus: { valueType: CreditSummaryValueType.TEXT },
    frequency: { valueType: CreditSummaryValueType.TEXT, filterType: FilterType.MULTI_SELECT, wrap: true, sortable: true },
    ownership: { valueType: CreditSummaryValueType.TEXT, filterType: FilterType.MULTI_SELECT, wrap: true, sortable: true },
    accountRemarks: { valueType: CreditSummaryValueType.TEXT, filterType: FilterType.MULTI_SELECT, wrap: true, sortable: true },

    // Dates — from/to range, sortable.
    lastReported: { valueType: CreditSummaryValueType.DATE, filterType: FilterType.DATE_RANGE, sortable: true },
    sanctionDate: {
        valueType: CreditSummaryValueType.DATE,
        filterType: FilterType.DATE_RANGE,
        sortable: true,
        rawValueKey: 'sanctionedDateActual',
    },

    // Amounts — min/max, sortable.
    sanctionedLimit: { valueType: CreditSummaryValueType.AMOUNT, filterType: FilterType.RANGE, sortable: true, unit: '₹' },
    outstandingAmount: { valueType: CreditSummaryValueType.AMOUNT, filterType: FilterType.RANGE, sortable: true, unit: '₹' },
    currentOverdue: {
        valueType: CreditSummaryValueType.AMOUNT,
        filterType: FilterType.RANGE,
        sortable: true,
        unit: '₹',
        tone: CreditSummaryCellTone.DANGER_WHEN_POSITIVE,
    },
    emi: { valueType: CreditSummaryValueType.AMOUNT, filterType: FilterType.RANGE, sortable: true, unit: '₹' },
    tenure: { valueType: CreditSummaryValueType.DURATION, filterType: FilterType.RANGE, sortable: true, labelSuffix: '(Months)' },

    // `repaymentHistory` ("Delays") is a run of month-by-month delay codes, not a scalar, so neither
    // a tick-list nor a range means anything for it - it gets no filter. It does sort: `-` counts as
    // missing and falls last, so sorting brings the accounts that actually have delays to the top.
    repaymentHistory: {
        valueType: CreditSummaryValueType.TEXT,
        tone: CreditSummaryCellTone.DANGER_WHEN_PRESENT,
        wrap: true,
        sortable: true,
    },
};

/** True for a cell the bureau did not report — polus writes `-`, and `sanctionDate` can be null. */
export function isMissingCellValue(raw: unknown): boolean {
    if (raw === null || raw === undefined) return true;
    const trimmed = String(raw).trim();
    return trimmed === '' || trimmed === MISSING_CELL_VALUE;
}

/**
 * `"₹ 25,00,000"` -> `2500000`. Returns null for a missing or unparseable cell so callers can sort
 * and filter it as "no value" rather than as zero.
 *
 * The numeric conversion itself reuses `parseINR`, but we pre-validate instead of leaning on its
 * `|| 0` fallback: that fallback turns anything unparseable into a legitimate-looking ₹0.
 */
export function parseAmountCell(raw: unknown): number | null {
    if (isMissingCellValue(raw)) return null;
    const cleaned = String(raw).replace(/[₹,\s]/g, '');
    if (cleaned === '' || Number.isNaN(Number(cleaned))) return null;
    return parseINR(String(raw));
}

/**
 * `"20 Feb 2022"` -> epoch ms, matching the epoch bounds DateRangeFilterRenderer emits.
 * Prefers `rawValue` (polus's real Date) when the column declares one.
 */
export function parseDateCell(raw: unknown, rawValue?: unknown): number | null {
    if (rawValue !== null && rawValue !== undefined && rawValue !== '') {
        const fromRaw = dayjs(rawValue as string);
        if (fromRaw.isValid()) return fromRaw.valueOf();
    }
    if (isMissingCellValue(raw)) return null;
    const parsed = dayjs(String(raw).trim(), CREDIT_SUMMARY_DATE_FORMAT, true);
    return parsed.isValid() ? parsed.valueOf() : null;
}

/** `"12 Months"` -> `12`. Numeric, so 9 sorts below 12 instead of lexically above it. */
export function parseDurationCell(raw: unknown): number | null {
    if (isMissingCellValue(raw)) return null;
    const matched = String(raw).match(/-?\d+(\.\d+)?/);
    return matched ? Number(matched[0]) : null;
}

/**
 * The comparable value behind a cell: a number for the scalar columns, the string itself for text,
 * and null where there is nothing to compare - an unreported cell, or a column with no behaviour
 * entry. Ordering happens in compareCellValues, which branches on the type.
 */
export function readComparableCellValue(row: any, field: string): number | string | null {
    const behaviour = CREDIT_SUMMARY_COLUMNS[field];
    if (!behaviour) return null;

    switch (behaviour.valueType) {
        case CreditSummaryValueType.AMOUNT:
            return parseAmountCell(row?.[field]);
        case CreditSummaryValueType.DATE:
            return parseDateCell(row?.[field], behaviour.rawValueKey ? row?.[behaviour.rawValueKey] : undefined);
        case CreditSummaryValueType.DURATION:
            return parseDurationCell(row?.[field]);
        case CreditSummaryValueType.TEXT:
            // Null rather than "" for an unreported cell, so it sorts last in both directions
            // alongside every other missing value instead of leading the ascending order.
            return isMissingCellValue(row?.[field]) ? null : String(row?.[field]);
        default:
            return null;
    }
}

/**
 * Orders two comparable cells. Text compares with `sensitivity: 'base'`, so "HDB" and "Hdb" sort
 * together rather than uppercase leading - the same call ManagementProfileTab uses.
 */
export function compareCellValues(a: number | string, b: number | string): number {
    if (typeof a === 'number' && typeof b === 'number') return a - b;
    return String(a).localeCompare(String(b), undefined, { sensitivity: 'base' });
}

/**
 * Tick-list options for a categorical column: the distinct values actually present in the rows.
 * Derived rather than hardcoded so the Investor Name list on a given company shows exactly the
 * lenders on that company's file — a fixed list would go stale the moment a new lender appeared.
 */
export function distinctColumnOptions(rows: any[], field: string): { label: string; value: string }[] {
    const seen = new Set<string>();
    (rows || []).forEach((row) => {
        const raw = row?.[field];
        if (isMissingCellValue(raw)) return;
        seen.add(String(raw).trim());
    });
    return Array.from(seen)
        .sort((a, b) => a.localeCompare(b))
        .map((value) => ({ label: value, value }));
}

/**
 * Builds the DataTableColumn list the shared filter drawer consumes, by merging the behaviour map
 * onto the headers polus sent. Unknown keys yield a plain column — no filter card, no sort icon.
 */
/**
 * The column contract is split across two registries: polus's CibilTableColumn enum decides which
 * columns exist and in what order, CREDIT_SUMMARY_COLUMNS below decides how each one behaves. A key
 * added on the polus side with no entry here still renders — silently, with no filter, no sort,
 * sized to its label rather than its values, and excluded from every section Total, which then
 * shows `-`. Warn so that drift surfaces at the first render instead of as a wrong number.
 */
function warnOnUnknownColumns(headers: CreditSummaryHeader[]): void {
    const unknown = headers.filter((header) => header?.key && !CREDIT_SUMMARY_COLUMNS[header.key]).map((header) => header.key);
    if (unknown.length) {
        // eslint-disable-next-line no-console
        console.warn(
            `[CreditSummaryTable] no CREDIT_SUMMARY_COLUMNS entry for: ${unknown.join(', ')}. ` +
                'These render without filter, sort or section totals — add them to CREDIT_SUMMARY_COLUMNS.',
        );
    }
}

export function buildCreditSummaryColumns(headers: CreditSummaryHeader[] | undefined, rows: CreditSummaryRow[]): DataTableColumn[] {
    warnOnUnknownColumns(headers || []);

    return (headers || []).map((header) => {
        const behaviour = CREDIT_SUMMARY_COLUMNS[header?.key];

        const column = {
            field: header?.key,
            headerLabel: header?.name,
            headerOriginalLabel: header?.name,
            type: DataTableColumnType.TEXT,
        } as DataTableColumn;

        if (!behaviour?.filterType) return column;

        column.filterConfig = {
            enabled: true,
            filterType: behaviour.filterType,
            label: behaviour.labelSuffix ? `${header?.name} ${behaviour.labelSuffix}` : header?.name,
            ...(behaviour.filterType === FilterType.MULTI_SELECT && {
                options: distinctColumnOptions(rows, header.key),
                excludedValues: [MISSING_CELL_VALUE],
            }),
        };
        if (behaviour.unit) column.unit = behaviour.unit;

        return column;
    });
}

/** Horizontal cell padding the width has to carry: 0.75rem either side, per .TableBodyRowItem. */
const COLUMN_PADDING_REM = 1.5;

/**
 * Room a sortable header needs for its SortIcon beside the label: 20px for the icon's box plus its
 * own 4px margin, measured in a browser. .SortableHeader carries no gap - it duplicated that margin.
 *
 * Without this a measured column is exactly as wide as its text and the icon has nowhere to sit.
 */
const SORT_ICON_REM = 1.5;

/**
 * Weight and size the header renders at - .TableHead is `font-weight: 600; font-size: 0.875rem`.
 *
 * In px, not rem: the measuring canvas is never inserted into the DOM, so a relative unit resolves
 * against the canvas element's own font-size - the canvas default of 10px, not the document root's
 * 16px. `0.875rem` parsed as 8.75px and every measured width came out ~62% of the real text.
 */
const HEADER_WEIGHT_AND_SIZE = '600 14px';

/** Used only where the document is unavailable (SSR, jsdom). Matches the body rule in index.scss. */
export const FALLBACK_FONT_FAMILY = "'Gilroy-Medium', sans-serif";

/**
 * The family the header actually inherits, read from the document rather than hardcoded.
 *
 * Hardcoding is what broke this: the constant named `Gilroy`, which is not a declared family - the
 * @font-face rules define Gilroy-Medium, Gilroy-Bold and so on - so canvas silently fell back to
 * sans-serif and measured a different typeface than the one on screen.
 */
export function headerFont(): string {
    if (typeof document === 'undefined' || !document.body) return `${HEADER_WEIGHT_AND_SIZE} ${FALLBACK_FONT_FAMILY}`;

    const family = getComputedStyle(document.body).fontFamily || FALLBACK_FONT_FAMILY;
    return `${HEADER_WEIGHT_AND_SIZE} ${family}`;
}

/**
 * Text measurement, same approach as SheetEditor's useColumnAutofit: one cached offscreen canvas,
 * measured in px against the real font.
 *
 * `ch` is not a substitute. It is the width of the `0` glyph, which in this face is far wider than
 * the average letter - "Investor Name" at 13ch renders about nineteen characters wide, which is the
 * slack that made the frozen columns look loose.
 *
 * The font is set per call rather than once at creation, so a measurement taken before the webfont
 * loads is corrected on the next render instead of being cached wrong for the session.
 *
 * Returns null where canvas is unavailable (jsdom, SSR); callers fall back to the ch estimate.
 */
let measureCtx: CanvasRenderingContext2D | null | undefined;
function measureTextPx(text: string): number | null {
    if (measureCtx === undefined) {
        measureCtx = typeof document === 'undefined' ? null : document.createElement('canvas').getContext('2d');
    }
    if (!measureCtx) return null;

    measureCtx.font = headerFont();
    const width = measureCtx.measureText(text).width;
    return width > 0 ? width : null;
}

/**
 * Widest a wrapping column may get before its text starts breaking onto a second line. Sized from
 * the design's two-line Delays and Account Remarks cells; without a cap a single long remark would
 * take a third of the table.
 */
const WRAP_MAX_CH = 28;

/**
 * What one column has to fit, in both units: measured pixels where canvas is available, and a `ch`
 * estimate as the fallback.
 *
 * Every width on the table - the columns, the sticky offsets and the table itself - comes from this
 * one function, and they are summed in the same unit. They used to disagree: the table width summed
 * header labels only, so with `table-layout: fixed` the browser shrank the columns back to fit and
 * the amounts truncated anyway.
 */
function columnUnits(header: CreditSummaryHeader | undefined, rows: CreditSummaryRow[]): { px: number; ch: number; rem: number } {
    const label = header?.name || '';
    const behaviour = CREDIT_SUMMARY_COLUMNS[header?.key as string];
    const rem = COLUMN_PADDING_REM + (behaviour?.sortable ? SORT_ICON_REM : 0);

    // Only the truncating columns are measured. They sit flush against their header, so the `ch`
    // estimate's slack was visible on them - `ch` is the width of the `0` glyph, far wider than the
    // average letter in this face. Every other column keeps the estimate: its slack is what lets a
    // value that renders wider than its character count still fit.
    if (behaviour?.truncate) {
        const px = measureTextPx(label);
        return px === null ? { px: 0, ch: label.length, rem } : { px, ch: 0, rem };
    }

    const longest = (rows || []).reduce((widest, row) => {
        const value = String(row?.[header?.key as string] ?? '');
        return value.length > widest.length ? value : widest;
    }, '');

    // A wrapping column stops growing at the cap and breaks onto a second line instead.
    const capped = behaviour?.wrap && longest.length > WRAP_MAX_CH ? longest.slice(0, WRAP_MAX_CH) : longest;

    return { px: 0, ch: Math.max(label.length, capped.length), rem };
}

/**
 * Sums a run of columns. Units are mixed by design - the truncating columns are measured in px, the
 * rest estimated in ch - so each is summed separately and calc() adds them.
 *
 * Every width on the table (the columns, the sticky offsets and the table itself) comes from here,
 * so they cannot drift apart. They used to: the table width summed header labels only, and with
 * `table-layout: fixed` the declared table width wins, squeezing the columns back.
 */
function widthAcross(columns: { px: number; ch: number; rem: number }[]): string {
    // Round per column, not the total: a column renders at its own rounded width, so rounding the
    // sum instead leaves the table a pixel or two narrower than its columns.
    const px = columns.reduce((total, c) => total + Math.ceil(c.px), 0);
    const ch = columns.reduce((total, c) => total + c.ch, 0);
    const rem = columns.reduce((total, c) => total + c.rem, 0);

    const terms = [px && `${px}px`, ch && `${ch}ch`, rem && `${rem}rem`].filter(Boolean);
    return terms.length ? `calc(${terms.join(' + ')})` : '0';
}

export function columnWidthFor(header: CreditSummaryHeader | undefined, rows: CreditSummaryRow[]): string {
    return widthAcross([columnUnits(header, rows)]);
}

export function stickyLeftOffset(headers: CreditSummaryHeader[] | undefined, index: number, rows: CreditSummaryRow[]): string {
    const preceding = (headers || []).slice(0, index);
    if (!preceding.length) return '0';

    return widthAcross(preceding.map((header) => columnUnits(header, rows)));
}

export function tableWidthFor(headers: CreditSummaryHeader[] | undefined, rows: CreditSummaryRow[]): string {
    return stickyLeftOffset(headers, (headers || []).length, rows);
}

/** Whether a column carries a sort control. */
export function isSortableColumn(field: string): boolean {
    return !!CREDIT_SUMMARY_COLUMNS[field]?.sortable;
}

/** True for a column whose cells wrap onto a second line instead of ellipsing. */
export function isWrappingColumn(field: string): boolean {
    return !!CREDIT_SUMMARY_COLUMNS[field]?.wrap;
}

export function dangerToneFor(row: CreditSummaryRow, field: string): CreditSummaryCellTone | null {
    const tone = CREDIT_SUMMARY_COLUMNS[field]?.tone;
    if (!tone) return null;

    if (tone === CreditSummaryCellTone.DANGER_WHEN_PRESENT) {
        return isMissingCellValue(row?.[field]) ? null : tone;
    }

    const amount = parseAmountCell(row?.[field]);
    return amount !== null && amount > 0 ? tone : null;
}

export const AMOUNT_TOTAL_FIELDS: string[] = Object.entries(CREDIT_SUMMARY_COLUMNS)
    .filter(([, behaviour]) => behaviour.valueType === CreditSummaryValueType.AMOUNT)
    .map(([field]) => field);

export function formatAmountTotal(total: number): string {
    return Math.round(total).toLocaleString('en-IN', { maximumFractionDigits: 0 });
}
